/**
 * Terminology layer of the skins (spec 7.1). Imported as
 * `@velora/contracts/skins/terms`.
 *
 * `VELORA_TERMS` is the one list of label keys and Velora's own wording.
 * Each PMS skin has its own map in `./terms/<skin>.ts` (owned by that skin's
 * agent) holding only the words that differ. UI code reads a label with
 * `useTerm('familyFile')` or `<Term k="familyFile" />`; nav items name a key
 * with `labelKey`.
 *
 * Adding a key: append it here with Velora's wording (integrator file;
 * request it in docs/requests/ when you do not own this file). Never
 * rename or remove a key a skin map or a page uses.
 */
export const VELORA_TERMS = {
  /* Modules (nav items, menu items, window titles) */
  home: 'Home',
  schedule: 'Schedule',
  familyFile: 'Patients',
  patientFile: 'Patient',
  ledger: 'Billing',
  chart: 'Charting',
  perio: 'Perio',
  txPlan: 'Treatment Plans',
  imaging: 'Imaging',
  recall: 'Recall',
  clinicalNotes: 'Notes',
  progressNotes: 'Progress notes',
  checkout: 'Checkout',
  reports: 'Analytics',
  communications: 'Communications',
  documents: 'Documents',
  contactLog: 'Contact log',
  /* People and records */
  patient: 'Patient',
  patients: 'Patients',
  selectPatient: 'Select patient',
  guarantor: 'Guarantor',
  headOfHouse: 'Guarantor',
  household: 'Household',
  chartNumber: 'Chart #',
  patientId: 'Patient ID',
  provider: 'Provider',
  operatory: 'Operatory',
  office: 'Office',
  newPatient: 'New patient',
  alerts: 'Alerts',
  /* Scheduling */
  appointment: 'Appointment',
  pinboard: 'Pinboard',
  unscheduledList: 'Unscheduled',
  asapList: 'ASAP list',
  breakAppointment: 'Break',
  setComplete: 'Complete',
  routeSlip: 'Route slip',
  dayNote: 'Day note',
  huddle: 'Huddle',
  statusArrived: 'Arrived',
  statusSeated: 'In chair',
  statusCheckout: 'Checkout',
  /* Money */
  payment: 'Payment',
  insurancePayment: 'Insurance payment',
  adjustment: 'Adjustment',
  writeoff: 'Write-off',
  statement: 'Statement',
  claim: 'Claim',
  paymentPlan: 'Payment plan',
  estInsurance: 'Ins est.',
  estPatient: 'Patient est.',
  daySheet: 'Day sheet',
  /* Clinical */
  procedureButtons: 'Quick buttons',
  procedureCodes: 'Procedure codes',
  txStatusPlanned: 'Treatment planned',
  txStatusComplete: 'Completed',
  txStatusExisting: 'Existing',
  txStatusExistingOther: 'Existing other',
  /* Shell menus ("&" marks the Alt access key) */
  menuFile: '&File',
  menuEdit: '&Edit',
  menuView: '&View',
  menuModules: '&Modules',
  menuTools: '&Tools',
  menuHelp: '&Help',
  menuVelora: 'V&elora',
  /* Appended 2026-10-12 (requests-dentrix I-12, requests-eaglesoft 4) */
  officeManager: 'Office',
  apptBook: 'Schedule',
  fastCheckout: 'Checkout',
  enterProcedure: 'Add procedure',
  treatmentManager: 'Treatment plans',
  continuingCareList: 'Recall list',
  statusLate: 'Late',
  confirmLeftMessage: 'Left message',
  confirmNoAnswer: 'Unreachable',
  confirmPhoneBusy: 'Line busy',
  confirmWaitingCallback: 'Waiting for a call back',
  confirmOther: 'Other',
  menuLists: '&Lists',
  menuReports: '&Reports',
  menuOnline: '&Online',
  menuWindow: '&Window',
  quickPick: 'Quick button',
  walkout: 'Walkout',
  medicalHistory: 'Medical history',
  enterPayment: 'Take payment',
  findNewTime: 'Find a new time',
  moveToPinboard: 'Move to pinboard',
} as const;

export type TermKey = keyof typeof VELORA_TERMS;
export const TERM_KEYS = Object.keys(VELORA_TERMS) as TermKey[];

/** A skin's wording: only the keys that differ from Velora. */
export type TermMap = Readonly<Partial<Record<TermKey, string>>>;

export function isTermKey(value: unknown): value is TermKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(VELORA_TERMS, value);
}

/** The label for `key` in `map`, falling back to Velora's wording. */
export function resolveTerm(map: TermMap | undefined, key: TermKey): string {
  return map?.[key] ?? VELORA_TERMS[key];
}
